import Product from '../../domains/Product/Product'
import {
  INTERNAL_INVENTORY_API,
  NORMAL_INVENTORY,
  SHOPIFY_API,
  ShopifyVariantDetails,
} from '../../domains/ProductPurchaseInfo'
import { ProductVariantOption } from '../../domains/ProductVariant'
import { isInNewCartExperiment } from '../../hooks/cart/hooks/new-cart-exp-helper'
import queryVariantBySelectedOptions from '../../hooks/shopify/queryVariantBySelectedOptions'
import queryVariantsByProductId from '../../hooks/shopify/queryVariantsByProductId'
import { getInventoryType, getPreOrderShippingDate } from '../../hooks/shopify/shopify-utils'
import { decodeShopifyIdFromGraphqlIdResponse } from '../../utils/decodeShopifyIdFromGraphqlIdResponse'
import ProductService from '../productService/ProductService'

import { getVariantDetailsByIdForProductVariants } from './NewCheckoutProductService'
import { ShopifyClientBuilder } from './ShopifyClientBuilder'

const getVariantsInfoByShopifyProductId = async (
  shopifyClient: any,
  shopifyProductId: number,
  shopifyVariantIdToPriceMap: Record<number, string>,
): Promise<ShopifyVariantDetails[]> => {
  try {
    const result = await queryVariantsByProductId(shopifyClient, {
      id: `gid://shopify/Product/${shopifyProductId}`,
    })
    const childVariants = result?.data?.node?.variants?.edges || []
    const variantInfo: ShopifyVariantDetails[] = []

    childVariants.forEach(variant => {
      const shopifyVariantIdAsString = decodeShopifyIdFromGraphqlIdResponse(variant.node.id)
      const shopifyVariantId = shopifyVariantIdAsString && parseInt(shopifyVariantIdAsString)
      if (!shopifyVariantId || isNaN(shopifyVariantId)) return

      const price = parseFloat(variant.node.priceV2.amount)

      variantInfo.push({
        id: shopifyVariantId,
        quantityAvailable: variant.node.quantityAvailable,
        price: isNaN(price) ? 0 : price,
        inventoryType: getInventoryType(variant.node),
        preOrderShippingDate: getPreOrderShippingDate(variant.node),
        inventoryInfoSource: SHOPIFY_API,
      })
    })
    return variantInfo
  } catch (e) {
    const variantInfoFromInventory = await ProductService.getProductVariantIdToInventoryCount(shopifyProductId)
    const childVariantIds: string[] = Object.keys(variantInfoFromInventory)
    const variantInfo: ShopifyVariantDetails[] = []
    childVariantIds.forEach(variantId => {
      const price = parseFloat(shopifyVariantIdToPriceMap[Number(variantId)])
      variantInfo.push({
        id: parseInt(variantId),
        quantityAvailable: variantInfoFromInventory[variantId],
        price: isNaN(price) ? 0 : price,
        inventoryType: NORMAL_INVENTORY,
        preOrderShippingDate: undefined,
        inventoryInfoSource: INTERNAL_INVENTORY_API,
      })
    })
    return variantInfo
  }
}

class ShopifyProductService {
  private shopifyClient: any

  async getShopifyVariantDetailsByIdForProductVariants(
    product: Product,
  ): Promise<Record<number, ShopifyVariantDetails>> {
    const isNewCart = isInNewCartExperiment()
    if (isNewCart) {
      return await getVariantDetailsByIdForProductVariants(product)
    }

    const allChildShopifyProductIds = Array.from(
      new Set(
        product.variants
          .map(v => v.shopifyProductId)
          .concat(...(product.childShopifyProductIds || []))
          .filter(id => product.shopifyProductId !== id),
      ),
    )

    const shopifyVariantIdToPriceMap: Record<number, string> = {}

    product.variants.forEach(({ price, shopifyVariantId }) => {
      shopifyVariantIdToPriceMap[shopifyVariantId] = price as unknown as string
    })

    let allVariantsDetail: ShopifyVariantDetails[] = []
    const fetchShopifyVariantsPromises: Promise<ShopifyVariantDetails[]>[] = [
      getVariantsInfoByShopifyProductId(this.getShopifyClient(), product.shopifyProductId, shopifyVariantIdToPriceMap),
    ]
    if (allChildShopifyProductIds.length > 0) {
      allChildShopifyProductIds.forEach(childShopifyPid => {
        fetchShopifyVariantsPromises.push(
          getVariantsInfoByShopifyProductId(this.getShopifyClient(), childShopifyPid, shopifyVariantIdToPriceMap),
        )
      })
    }
    const variantsDetailDataArr: ShopifyVariantDetails[][] = await Promise.all(fetchShopifyVariantsPromises)
    variantsDetailDataArr.forEach(variantsData => {
      allVariantsDetail = [...allVariantsDetail, ...variantsData]
    })

    const shopifyVariantIdVsShopifyVariantInfo: Record<number, ShopifyVariantDetails> = {}

    allVariantsDetail.forEach(variantDetail => {
      const shopifyVariantId = variantDetail.id
      shopifyVariantIdVsShopifyVariantInfo[shopifyVariantId] = variantDetail
    })
    return shopifyVariantIdVsShopifyVariantInfo
  }

  async getAvailableInventoryForSelectedOption(
    product: Product,
    selectedOptions: ProductVariantOption[],
    handle: string,
  ): Promise<number> {
    try {
      const {
        data: {
          product: {
            variantBySelectedOptions: { quantityAvailable },
          },
        },
      } = await queryVariantBySelectedOptions(this.getShopifyClient(), {
        id: `gid://shopify/Product/${product.shopifyProductId}`,
        selectedOptions: selectedOptions.map(option => {
          return {
            name: option.name,
            value: option.value,
          }
        }),
        handle,
      })
      return quantityAvailable
    } catch (e) {
      return 0
    }
  }

  private getShopifyClient() {
    if (!this.shopifyClient) {
      this.shopifyClient = ShopifyClientBuilder.getShopifyClient()
    }
    return this.shopifyClient
  }
}

const shopifyProductService = new ShopifyProductService()

export default shopifyProductService
